// Tesla Timeline Viewer App v1.0.0
import { useEffect, useCallback, useRef } from 'react'
import { useViewerStore } from './stores/viewerStore'
import Timeline from './components/Timeline'
import VideoGrid from './components/VideoGrid'
import Sidebar from './components/Sidebar'
import PlaybackControls from './components/PlaybackControls'
import { fetchTimeline, getClipsAtTime } from './api/client'
import type { CameraChannel } from './types'

export default function App() {
  const {
    currentTime,
    setCurrentTime,
    timelineStart,
    timelineEnd,
    selectedChannels,
    eventTypeFilter,
    threatLevelFilter,
    showOnlyWithDetections,
    setSegments,
    currentClips,
    setCurrentClip,
    isPlaying,
    playbackSpeed,
    showSidebar,
    toggleSidebar,
    isLoading,
    loadingMessage,
    setLoading,
    goToTime,
  } = useViewerStore()

  const playbackIntervalRef = useRef<number | null>(null)
  const lastFetchTimeRef = useRef<number>(0)

  // Fetch timeline data when filters change
  useEffect(() => {
    const loadTimeline = async () => {
      setLoading(true, 'Loading timeline...')
      try {
        const response = await fetchTimeline({
          startDate: timelineStart.toISOString(),
          endDate: timelineEnd.toISOString(),
          channels: selectedChannels,
          eventTypes: eventTypeFilter,
          threatLevels: threatLevelFilter,
          hasDetections: showOnlyWithDetections,
        })
        setSegments(response.segments)
      } catch (error) {
        console.error('Failed to load timeline:', error)
      } finally {
        setLoading(false)
      }
    }

    loadTimeline()
  }, [timelineStart, timelineEnd, selectedChannels, eventTypeFilter, threatLevelFilter, showOnlyWithDetections, setSegments, setLoading])

  // Fetch clips at current time
  const fetchClipsAtTime = useCallback(
    async (time: Date) => {
      // Debounce: don't fetch more than every 500ms
      const now = Date.now()
      if (now - lastFetchTimeRef.current < 500) return
      lastFetchTimeRef.current = now

      try {
        const response = await getClipsAtTime({
          time: time.toISOString(),
          channels: selectedChannels,
        })

        // Update clips for each channel
        for (const channel of selectedChannels) {
          const clip = response.clips[channel] || null
          setCurrentClip(channel, clip)
        }
      } catch (error) {
        console.error('Failed to fetch clips:', error)
      }
    },
    [selectedChannels, setCurrentClip]
  )

  // Fetch clips when time changes significantly
  useEffect(() => {
    fetchClipsAtTime(currentTime)
  }, [currentTime, fetchClipsAtTime])

  // Playback timer
  useEffect(() => {
    if (isPlaying) {
      playbackIntervalRef.current = window.setInterval(() => {
        const newTime = new Date(currentTime.getTime() + 100 * playbackSpeed)
        setCurrentTime(newTime)
      }, 100)
    } else if (playbackIntervalRef.current) {
      clearInterval(playbackIntervalRef.current)
      playbackIntervalRef.current = null
    }

    return () => {
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current)
      }
    }
  }, [isPlaying, playbackSpeed, currentTime, setCurrentTime])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return

      switch (e.key) {
        case ' ':
          e.preventDefault()
          useViewerStore.getState().togglePlayback()
          break
        case 's':
          e.preventDefault()
          toggleSidebar()
          break
        case 'Escape':
          useViewerStore.getState().setSelectedDetection(null)
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [toggleSidebar])

  // Handle timeline time change
  const handleTimeChange = useCallback(
    (time: Date) => {
      setCurrentTime(time)
    },
    [setCurrentTime]
  )

  // Handle clip ended - advance to next
  const handleClipEnded = useCallback(
    async (channel: CameraChannel) => {
      const currentClip = currentClips.get(channel)
      if (currentClip) {
        // Move time forward to end of clip
        const endTime = new Date(currentClip.endTime)
        goToTime(endTime)
      }
    },
    [currentClips, goToTime]
  )

  return (
    <div className="h-screen flex flex-col bg-viewer-bg text-viewer-text overflow-hidden">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-2 bg-viewer-surface border-b border-viewer-border">
        <div className="flex items-center gap-3">
          <svg className="w-8 h-8 text-viewer-danger" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
          </svg>
          <h1 className="text-lg font-semibold">Tesla Timeline Viewer</h1>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={toggleSidebar}
            className="p-2 rounded hover:bg-viewer-surface-hover"
            title="Toggle sidebar (S)"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Video area */}
        <main className="flex-1 flex flex-col overflow-hidden">
          {/* Video grid */}
          <VideoGrid
            clips={currentClips}
            onClipEnded={handleClipEnded}
          />

          {/* Playback controls */}
          <PlaybackControls />

          {/* Timeline */}
          <Timeline onTimeChange={handleTimeChange} />
        </main>

        {/* Sidebar */}
        {showSidebar && <Sidebar />}
      </div>

      {/* Loading overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-viewer-surface rounded-lg p-6 flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-3 border-viewer-accent border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">{loadingMessage || 'Loading...'}</span>
          </div>
        </div>
      )}
    </div>
  )
}
